import { getListingLayout, isNewsListingTemplate, LAYOUT, PAGE_PARAM } from 'app/utilities/news-listing';

const DOMAIN = 'https://www.aucklandzoo.co.nz';

/**
 * Returns the path of the news/article entity itself, ignoring any listing state.
 *
 * @param {string} slug Page slug
 * @param {string} templateType Page template type
 * @param {string} parent Parent category slug, for level 2 categories
 *
 * @return {string|null} Path, or null for non-news/article pages
 */
const getEntityPath = (slug, templateType, parent) => {
    if (templateType === 'news') {
        return '/news';
    }

    // A page of its own rather than one under `/news`, and the only listing whose
    // path is its slug
    if (templateType === 'news-archive') {
        return '/news-archive';
    }

    if (!slug) {
        return null;
    }

    if (templateType === 'news-sub-category') {
        return parent ? `/news/${parent}/${slug}` : `/news/${slug}`;
    }

    if (templateType !== 'article' && templateType !== 'news-category') {
        return null;
    }

    return `/news/${slug}`;
};

/**
 * Returns the canonical URL for news/article pages only.
 *
 * A listing filtered by one category duplicates that category's own page, so it
 * points there to consolidate signals instead of competing with it. Filtering by
 * several has no single page behind it, and the combinations multiply, so those
 * point back at the unfiltered listing rather than becoming indexable URLs of
 * their own. Either way the page number is left off, because the filtered views
 * are not the copy of the listing that should be walked.
 *
 * An unfiltered `?page=N` is the one that is, so it points at itself. Pointing it
 * at page one instead would tell crawlers to drop it, and with it the only trail
 * to the articles that are only linked from that page.
 *
 * @param {string} slug Page slug
 * @param {string} templateType Page template type
 * @param {string} parent Parent category slug, for level 2 categories
 * @param {object} listing Listing state, `{ activeCategories, page }`
 *
 * @return {string|null} Canonical URL or null for non-news/article pages
 */
export default function getCanonicalUrl(slug, templateType, parent, listing = {}) {
    const path = getEntityPath(slug, templateType, parent);

    if (!path) {
        return null;
    }

    if (!isNewsListingTemplate(templateType)) {
        return `${DOMAIN}${path}`;
    }

    const { activeCategories = [], page = 1 } = listing;

    if (activeCategories.length === 1) {
        return `${DOMAIN}${path}/${activeCategories[0]}`;
    }

    if (activeCategories.length) {
        return `${DOMAIN}${path}`;
    }

    /*
        The grid's pages contain one another: page three holds everything pages one
        and two do, and every page past the end of the listing holds exactly what
        the last one holds. Not one of them is a distinct copy of the listing, so
        pointing them at themselves would be publishing the same articles under
        hundreds of URLs. They all point at the clean path instead.

        They stay crawlable, which is the part that matters: the chain of "More
        stories" links is how the older articles are reached, and it is those
        articles rather than the pages assembling them that should be indexed.
    */
    if (getListingLayout(templateType) === LAYOUT.GRID) {
        return `${DOMAIN}${path}`;
    }

    return page > 1 ? `${DOMAIN}${path}?${PAGE_PARAM}=${page}` : `${DOMAIN}${path}`;
}
